import { Building2, Folder, FileText, Square } from 'lucide-react'
import type { VaultNode } from '@/lib/api'

/** A small type-distinct icon so domain / workstream / block / card read
 * differently at a glance. Color-coded: domain indigo, workstream amber,
 * block slate, card muted. */
export function NodeIcon({ type, className = '' }: { type: VaultNode['node_type']; className?: string }) {
  const common = `shrink-0 ${className}`
  switch (type) {
    case 'domain':
      return <Building2 size={14} className={`${common} text-indigo-400`} />
    case 'workstream':
      return <Folder size={14} className={`${common} text-amber-400`} />
    case 'block':
      return <Square size={11} className={`${common} text-slate-400`} />
    default:
      return <FileText size={13} className={`${common} text-muted-foreground`} />
  }
}

/** Weight/size that reinforces the hierarchy in lists. */
export function nodeTextClass(type: VaultNode['node_type']): string {
  switch (type) {
    case 'domain':
      return 'font-semibold text-foreground'
    case 'workstream':
      return 'font-medium text-foreground/90'
    case 'block':
      return 'text-foreground/80'
    default:
      return 'text-muted-foreground'
  }
}

/** Status → label + dark-theme pill classes. Mirrors the dashboards' palette
 * (completed=blue, in_progress=yellow, scheduled=green, pending=gray,
 * overdue=red, missing=orange). */
const STATUS_STYLE: Record<string, { label: string; cls: string }> = {
  overdue: { label: 'overdue', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  missing: { label: 'missing', cls: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  in_progress: { label: 'in progress', cls: 'bg-yellow-500/15 text-yellow-200 border-yellow-500/30' },
  completed: { label: 'completed', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  scheduled: { label: 'scheduled', cls: 'bg-green-500/10 text-green-300 border-green-500/30' },
  pending: { label: 'pending', cls: 'bg-neutral-500/15 text-neutral-300 border-neutral-500/30' },
}

export function StatusPill({ status, wrapped }: { status: string | null; wrapped?: boolean | null }) {
  if (!status) return null
  const s = STATUS_STYLE[status] ?? { label: status, cls: 'bg-neutral-500/15 text-neutral-300 border-neutral-500/30' }
  const label = status === 'completed' && wrapped === false ? 'completed · unwrapped' : s.label
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium leading-none ${s.cls}`}>
      {label}
    </span>
  )
}

const EXT_BADGE: Record<string, string> = {
  md: 'bg-blue-500/15 text-blue-300',
  pdf: 'bg-red-500/15 text-red-300',
  png: 'bg-emerald-500/15 text-emerald-300',
  jpg: 'bg-emerald-500/15 text-emerald-300',
  jpeg: 'bg-emerald-500/15 text-emerald-300',
  gif: 'bg-emerald-500/15 text-emerald-300',
  webp: 'bg-emerald-500/15 text-emerald-300',
  svg: 'bg-emerald-500/15 text-emerald-300',
  xlsx: 'bg-amber-500/15 text-amber-300',
  xls: 'bg-amber-500/15 text-amber-300',
  csv: 'bg-amber-500/15 text-amber-300',
  docx: 'bg-violet-500/15 text-violet-300',
  doc: 'bg-violet-500/15 text-violet-300',
  eml: 'bg-slate-500/20 text-slate-300',
  msg: 'bg-slate-500/20 text-slate-300',
}

export function ExtTag({ ext }: { ext: string | null }) {
  if (!ext) return null
  const key = ext.replace('.', '').toLowerCase()
  const cls = EXT_BADGE[key] ?? 'bg-neutral-600/30 text-neutral-400'
  return (
    <span className={`rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${cls}`}>{key}</span>
  )
}

// This system is EDT (America/New_York), never UTC. Two rules follow from that:
//   1. Date-only values (all-day blocks, "YYYY-MM-DD") are a wall-calendar day,
//      NOT an instant. `new Date("2026-06-14")` parses as UTC midnight, which is
//      the evening BEFORE in EDT — so it renders a day early. Build the Date
//      from local components instead so the day never shifts.
//   2. Timed values carry an offset; format them pinned to America/New_York so
//      the time reads as EDT regardless of the viewer's browser timezone.
const NY_TZ = 'America/New_York'

/** Parse a vault calendar value (calendar_date / calendar_end) to a Date,
 *  treating date-only strings as the local wall day to avoid the UTC off-by-one. */
export function parseVaultDate(s: string | null | undefined): Date | null {
  if (!s) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(s)
  if (m && m[4] === undefined) {
    // date-only → local wall day
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  }
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Compact "when" from a block's calendar fields. */
export function formatWhen(node: VaultNode): string {
  if (!node.calendar_date) return ''
  const raw = node.calendar_date
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw)
  if (node.calendar_all_day || dateOnly) {
    // All-day blocks are a wall-calendar DAY, not an instant. The API serves
    // calendar_date from a `timestamptz` column, so an all-day "2026-06-15"
    // arrives as "2026-06-15T00:00:00+00:00" (UTC midnight) — parsing that as an
    // instant renders a day early in EDT (the Jun 15 → "Jun 14" bug). Take ONLY
    // the leading Y-M-D and build a LOCAL date so the day can never shift,
    // regardless of any time/zone suffix the value carries.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw)
    if (!m) return ''
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return ''
  // Timed: pin to EDT so the time is identical everywhere, never UTC/browser-local.
  const day = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: NY_TZ })
  const t = d
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: NY_TZ })
    .replace(':00', '')
    .replace(' ', '')
    .toLowerCase()
  return `${day} ${t}`
}

export interface ChildGroup {
  key: string
  label: string
  nodes: VaultNode[]
  collapsed: boolean
}

const completedDate = (n: VaultNode) => n.calendar_end ?? n.calendar_date ?? null

/** Alphabetical (domains, workstreams). */
export const byNameAsc = (nodes: VaultNode[]) => [...nodes].sort((a, b) => a.name.localeCompare(b.name))

/** Active blocks: by When ascending (soonest first), undated last. */
export const sortActiveBlocks = (nodes: VaultNode[]) => {
  const t = (n: VaultNode) => parseVaultDate(n.calendar_date)?.getTime() ?? Infinity
  return [...nodes].sort((a, b) => t(a) - t(b) || a.name.localeCompare(b.name))
}

/** Archived / inactive: completed-date DESC (dated first), then the rest (missing
 * blocks, loose cards) by filename DESC. */
export const sortInactive = (nodes: VaultNode[]) => {
  const dated = nodes.filter((n) => completedDate(n))
  const undated = nodes.filter((n) => !completedDate(n))
  dated.sort(
    (a, b) =>
      (parseVaultDate(completedDate(b))?.getTime() ?? 0) -
      (parseVaultDate(completedDate(a))?.getTime() ?? 0)
  )
  undated.sort((a, b) => b.name.localeCompare(a.name))
  return [...dated, ...undated]
}

/** Split a node's children into the Active / Inactive / Archived hierarchy that
 * mirrors Obsidian. Domain → workstreams (active = has a non-archived block,
 * inactive = none, archived). Workstream → Active blocks / Cards / Archived
 * blocks. Block → flat cards. Empty groups are dropped by the caller. */
export function groupChildren(parentType: string, children: VaultNode[]): ChildGroup[] {
  if (parentType === 'domain') {
    const active: VaultNode[] = []
    const inactive: VaultNode[] = []
    const archived: VaultNode[] = []
    for (const c of children) {
      if (c.is_archived) archived.push(c)
      else if ((c.active_block_count ?? 0) > 0) active.push(c)
      else inactive.push(c)
    }
    return [
      { key: 'active', label: 'Active', nodes: byNameAsc(active), collapsed: false },
      { key: 'inactive', label: 'Inactive', nodes: byNameAsc(inactive), collapsed: true },
      { key: 'archived', label: 'Archived', nodes: byNameAsc(archived), collapsed: true },
    ]
  }
  if (parentType === 'workstream') {
    const active: VaultNode[] = []
    const archived: VaultNode[] = []
    const cards: VaultNode[] = []
    for (const c of children) {
      if (c.node_type === 'card') {
        if (c.name !== 'CONCIERGE') cards.push(c)
      } else if (c.is_archived) archived.push(c)
      else active.push(c)
    }
    return [
      { key: 'active', label: 'Active', nodes: sortActiveBlocks(active), collapsed: false },
      // Blockless ("orphaned") cards are tucked into a collapsed fold so the
      // active workstream view is just blocks. Display-only — files don't move.
      { key: 'cards', label: 'Loose cards', nodes: byNameAsc(cards), collapsed: true },
      { key: 'archived', label: 'Archived', nodes: sortInactive(archived), collapsed: true },
    ]
  }
  return [{ key: 'all', label: 'Cards', nodes: children, collapsed: false }]
}

export function nodeGlyph(node: VaultNode): string {
  switch (node.node_type) {
    case 'domain':
      return '◆'
    case 'workstream':
      return '▸'
    case 'block':
      return '■'
    default:
      return '·'
  }
}
